import { useState } from 'react';
import cx from 'classnames';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faMaximize, faMinimize, faShareNodes, faGear } from '@fortawesome/pro-solid-svg-icons';
import { faCube } from '@fortawesome/pro-regular-svg-icons';

import { useAppSelector, isEmbeddedSelector } from '../../../store';
import { isFullscreenAvailable } from '../../../utils';
import { LayoutButton } from './LayoutButton';
import { LightsMenu } from './LightsMenu';
import { SizesMenu } from './SizesMenu';
import { AdminMenu } from './AdminMenu';
import { Tooltip, TooltipTrigger, TooltipContent } from '../../Tooltip';

import './VrMenu.scss';

export function VrMenu({
  isOpen,
  setIsOpen,
  layouts,
  activeLayout,
  onSelectLayout,
  lights,
  activeLight,
  onSelectLight,
  sizes,
  activeSize,
  onSelectSize,
  adminMode,
  toggleAdminMode,
  modelID,
  views,
  activeView,
  setActiveView,
  defaultView,
  setDefaultView,
  links,
  setHoveredLinkID,
  onDragLink,
  isFullscreen,
  onToggleFullscreen,
  showAdmin,
  setShareIsOpen,
}) {
  const isEmbedded = useAppSelector(isEmbeddedSelector)
    || window.location !== window.parent.location;

  const hasLayouts = layouts.length > 1;
  const hasLights = lights.length > 1;
  const hasSizes = sizes.length > 1;
  const hasEmptyLayout = layouts.includes('Empty');

  // VrMenu is open or collapsed
  const [adminIsOpen, setAdminIsOpen] = useState(adminMode);

  const toggleAdminMenu = () => {
    if (!isOpen && adminIsOpen) {
      // Menu minimized but admin open, expand menu
      setIsOpen(true);
    } else {
      if (!hasLayouts || !isOpen) {
        setIsOpen(!adminIsOpen);
      }
      setTimeout(
        () => {
          setAdminIsOpen(!adminIsOpen);
          toggleAdminMode();
        },
        adminIsOpen && !hasLayouts ? '1000' : '0',
      );
    }
  };

  // Sort Layouts, Empty first
  const sortedLayouts = [...layouts].sort((a, b) => {
    if (a === 'Empty') return -1;
    if (b === 'Empty') return 1;
    return a.localeCompare(b);
  });

  return (
    <div
      id="vr-menu"
      className={cx({ open: isOpen })}
    >
      <div id="vr-menu-top-bar">
        <div id="left-side-buttons">
          {(hasLayouts || adminIsOpen) && (
            <div
              id="toggle-vr-menu"
              className="vr-menu-button"
              onClick={() => setIsOpen(!isOpen)}
            >
              <FontAwesomeIcon icon={faAngleDown} className="fa-xl" />
            </div>
          )}
        </div>

        <div id="right-side-buttons">
          {hasSizes && (
            <SizesMenu
              sizes={sizes}
              activeSize={activeSize}
              onSelectSize={onSelectSize}
            />
          )}
          {hasLayouts && hasEmptyLayout && (
            <Tooltip placement='top'>
              <TooltipTrigger>
                <div
                  className="vr-menu-button"
                  onClick={() => onSelectLayout("Empty")}
                >
                  <FontAwesomeIcon
                    icon={faCube}
                    className="fa-xl"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>Empty</TooltipContent>
            </Tooltip>
          )}
          {hasLights && (
            <LightsMenu
              lights={lights}
              activeLight={activeLight}
              onSelectLight={onSelectLight}
            />
          )}
          <Tooltip placement="top">
            <TooltipTrigger>
              <div
                className="vr-menu-button"
                onClick={() => setShareIsOpen(true)}
              >
                <FontAwesomeIcon icon={faShareNodes} className='fa-xl' />
              </div>
            </TooltipTrigger>
            <TooltipContent>Share</TooltipContent>
          </Tooltip>
          { (isFullscreenAvailable() || isEmbedded) && (
            <Tooltip placement="top">
              <TooltipTrigger>
                <div
                  className="vr-menu-button"
                  onClick={onToggleFullscreen}
                >
                  <FontAwesomeIcon
                    icon={isFullscreen ? faMinimize : faMaximize}
                    className='fa-xl'
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>Fullscreen</TooltipContent>
            </Tooltip>
          )}
          {showAdmin && (
            <Tooltip placement="top">
              <TooltipTrigger>
                <div
                  className="vr-menu-button"
                  onClick={toggleAdminMenu}
                >
                  <FontAwesomeIcon
                    icon={faGear}
                    className={cx('fa-xl', { open: adminIsOpen })}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>Settings</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      <div id="vr-menu-main">
        {adminIsOpen && (
          <AdminMenu
            layouts={sortedLayouts}
            activeLayout={activeLayout}
            onSelectLayout={onSelectLayout}
            views={views}
            activeView={activeView}
            setActiveView={setActiveView}
            defaultView={defaultView}
            setDefaultView={setDefaultView}
            links={links}
            setHoveredLinkID={(linkID) => setHoveredLinkID(linkID)}
            onDragLink={(viewId, name) => onDragLink(viewId, name)}
            modelID={modelID}
          />
        )}
        {!adminIsOpen &&
          hasLayouts &&
          sortedLayouts.map((name) => {
            return (
              <LayoutButton
                key={name}
                name={name}
                active={name === activeLayout}
                onSelectLayout={onSelectLayout}
              />
            );
          })}
      </div>
    </div>
  );
}
